import styled from '@emotion/styled'

import {hexToRGBAString} from '@vio/core/src/utils/css'

import {cssTheme} from '../../../themes'
import {linariaCustomMq, linariaMq} from '../../../utils/breakpoints'
import mirror from '../../../utils/mirror'
import {NoStyleButton} from '../helpers/NoStyleButton'
import {iconSizeMap} from '../Icon'

const MAIN_IMAGE_HEIGHT = 340
const CHEVRON_SIZE = iconSizeMap.lg

export const ImageRefContainer = styled.div<{selected: boolean}>`
  text-align: center;
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  overflow: hidden;
  opacity: ${({selected}) => (selected ? 1 : 0)};
  transition: opacity 0.6s linear;
`

export const ImageContainer = styled.div`
  img {
    display: block;
    min-height: $mainImageHeight;
    max-width: 100%;
    margin: auto;
    object-fit: cover;
  }
`

export const ImagesNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  z-index: 2;
  opacity: 0;
  text-align: center;
  transition: opacity 0.2s ease;
  color: ${cssTheme.colors.gallery.content};
  bottom: ${cssTheme.layout.spacing.s400};
  left: calc(50% - ${cssTheme.layout.spacing.s800});
  padding: ${cssTheme.layout.spacing.s200};
  min-width: calc(${cssTheme.layout.spacing.s300} * 10);
  font-size: ${cssTheme.fonts.size.md};
  line-height: ${cssTheme.fonts.lineHeight.xs};
  font-weight: ${cssTheme.fonts.weight.lg};
  filter: drop-shadow(
    0 0 2px ${hexToRGBAString(cssTheme.colors.gallery.filter, 0.5)}
  );

  > svg {
    margin-bottom: ${cssTheme.layout.spacing.s100};
    margin-right: ${cssTheme.layout.spacing.s200};
  }
`

export const ImagesWrapper = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
`

export const BaseArrow = styled.div`
  color: ${cssTheme.colors.gallery.content};
  position: absolute;
  top: calc(50% - ${CHEVRON_SIZE / 2}px);
  z-index: 1;
  cursor: pointer;

  filter: drop-shadow(
    0 0 2px ${hexToRGBAString(cssTheme.colors.gallery.filter, 0.5)}
  );
`

export const StyledArrowRight = styled(BaseArrow)`
  right: ${cssTheme.layout.spacing.s300};
  ${mirror()}
`

export const StyledArrowLeft = styled(BaseArrow)`
  left: ${cssTheme.layout.spacing.s300};
  ${mirror()}
`

export const ImageGalleryMain = styled.div<{
  fullWidth: boolean
  height: number
}>`
  display: inline-block;
  overflow: hidden;

  position: relative;
  background: ${cssTheme.colors.gallery.background};
  z-index: 0;
  width: ${({fullWidth}) => (fullWidth ? '100%' : '64%')};
  height: ${({height}) => height || MAIN_IMAGE_HEIGHT}px;
  border-radius: ${cssTheme.layout.radius.md};

  &.--wide {
    ${linariaMq.desktopXs} {
      border-bottom-left-radius: ${cssTheme.layout.spacing.s300};
      flex: ${({fullWidth}) => (fullWidth ? '100%' : '1.5')};
    }
  }

  &:not(&.--wide) {
    ${linariaMq.desktopXs} {
      border-bottom-left-radius: ${cssTheme.layout.spacing.s300};
      flex: 1;
    }
  }
`

export const ImageGallery = styled.section`
  background: white;
  &:hover ${ImagesNumber} {
    opacity: 1;
  }
  ${linariaMq.desktopXs} {
    display: flex;
  }
`

export const CenterClickArea = styled(NoStyleButton)`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 8;

  /* Restore button interaction */
  cursor: initial;
  -webkit-tap-highlight-color: initial;
  outline: initial;
`

export const NextClickArea = styled.div<{hasCenterClick: boolean}>`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: ${({hasCenterClick}) => (hasCenterClick ? '33%' : '70%')};
  height: 100%;
  z-index: 9;
  cursor: pointer;
`

export const PrevClickArea = styled.div<{hasCenterClick: boolean}>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${({hasCenterClick}) => (hasCenterClick ? '33%' : '30%')};
  height: 100%;
  z-index: 9;
  cursor: pointer;
`

export const ThumbsWrapper = styled.div`
  position: relative;
  transition: top 0.4s ease;
  display: flex;
  flex-flow: row wrap;
  width: 103%;
`

export const ThumbsContainer = styled.div<{
  hidden: boolean
}>`
  position: relative;
  display: ${({hidden}) => (hidden ? 'none' : 'inline-block')};
  vertical-align: top;
  box-sizing: border-box;
  overflow: hidden;
  padding-left: ${cssTheme.layout.spacing.s300};

  &.--wide {
    max-height: 393px;
    width: 190px;

    ${linariaCustomMq(768)} {
      width: 237px;
    }

    ${linariaMq.desktopXs} {
      width: 282px;
    }

    ${linariaMq.desktopSm} {
      width: 341px;
    }

    ${linariaCustomMq(1024)} {
      width: 475px;
    }

    ${linariaMq.desktopMd} {
      width: 542px;
    }
  }

  &:not(&.--wide) {
    max-height: 340px;
    width: 240px;

    ${linariaMq.desktopXs} {
      width: 280px;
    }
    ${linariaMq.desktopMd} {
      width: 326px;
    }
  }
`

export const Thumb = styled.div`
  display: inline-block;
  opacity: 1;
  border-radius: 1px;
  cursor: pointer;
  transition: border 0.1s ease;
  padding: 2px 4px;
  overflow: hidden;

  & > div {
    display: inline-block;
    max-width: 100%;
  }

  &.--active > div {
    box-sizing: border-box;
    border: solid 1px ${cssTheme.colors.gallery.border};
    box-shadow: 0px 0px 0px 2px ${cssTheme.colors.gallery.shadow};
  }

  &:hover {
    & > div {
      border: 2px outline ${cssTheme.colors.gallery.border};
    }
  }
`
